import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { Send, Calendar, ShieldCheck } from "lucide-react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function SalesTaxReportPage() {
    const now = new Date();
    // Default to previous month (that's what the cron reports on)
    const defaultDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const [year, setYear] = useState(defaultDate.getFullYear());
    const [month, setMonth] = useState(defaultDate.getMonth() + 1);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/sales-tax", { params: { year, month } });
            setReport(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not load report");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

    const sendNow = async () => {
        if (!window.confirm(
            `Email the ${MONTHS[month-1]} ${year} report right now to your report recipients?`
        )) return;
        setSending(true);
        try {
            const { data } = await api.post("/reports/sales-tax/send", { year, month });
            const suppressed = (data.emailed_to || []).filter((r) => r.status === "suppressed").length;
            if (suppressed) {
                toast.info(`Report generated. ${suppressed} recipient(s) were held back by the notification kill switch — flip the whitelist to send for real.`);
            } else {
                toast.success(`Sales-tax report emailed to ${data.emailed_to?.length || 0} recipient(s).`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Send failed");
        } finally { setSending(false); }
    };

    const backfillTax = async () => {
        if (!window.confirm("Look up county + tax rate for every customer whose fields aren't set yet? (May take a minute.)")) return;
        try {
            const { data } = await api.post("/customers/backfill-tax");
            toast.success(`Updated ${data.updated} customer(s) with county + tax rate`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Backfill failed");
        }
    };

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <PageHeader
                title="Monthly sales tax report"
                subtitle="Total sales, taxable sales, and tax collected — broken down by county. Auto-emails on the 10th of every month."
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={backfillTax} data-testid="salestax-backfill-btn">
                            <ShieldCheck className="w-3.5 h-3.5 mr-1" />
                            Enrich customer counties
                        </Button>
                        <Button onClick={sendNow} disabled={sending || !report?.counties?.length} data-testid="salestax-send-btn">
                            <Send className="w-3.5 h-3.5 mr-1" />
                            {sending ? "Sending…" : "Send report now"}
                        </Button>
                    </div>
                }
            />

            <Card className="mb-4">
                <CardContent className="p-4 flex items-center gap-4 flex-wrap">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <select
                        value={month}
                        onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                        className="text-sm px-3 py-1.5 rounded-md border border-input bg-transparent"
                        data-testid="salestax-month-select"
                    >
                        {MONTHS.map((n, i) => (<option key={i} value={i + 1}>{n}</option>))}
                    </select>
                    <select
                        value={year}
                        onChange={(e) => setYear(parseInt(e.target.value, 10))}
                        className="text-sm px-3 py-1.5 rounded-md border border-input bg-transparent"
                        data-testid="salestax-year-select"
                    >
                        {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                    <div className="text-xs text-muted-foreground ml-auto">
                        Sent to the report recipients configured for your company
                    </div>
                </CardContent>
            </Card>

            {loading && <div className="text-sm text-muted-foreground p-8 text-center">Loading…</div>}

            {report && !loading && (
                <Card>
                    <CardContent className="p-0">
                        <table className="w-full text-sm" data-testid="salestax-table">
                            <thead>
                                <tr className="border-b border-border bg-secondary/30">
                                    <th className="text-left px-4 py-3 font-medium">County</th>
                                    <th className="text-right px-4 py-3 font-medium">Invoices</th>
                                    <th className="text-right px-4 py-3 font-medium">Total sales</th>
                                    <th className="text-right px-4 py-3 font-medium">Taxable sales</th>
                                    <th className="text-right px-4 py-3 font-medium text-emerald-600">Tax collected</th>
                                </tr>
                            </thead>
                            <tbody>
                                {report.counties.length === 0 && (
                                    <tr><td colSpan={5} className="text-center px-4 py-8 text-muted-foreground">No invoices issued in this period.</td></tr>
                                )}
                                {report.counties.map((row) => (
                                    <tr key={row.county} className="border-b border-border" data-testid={`salestax-row-${row.county.toLowerCase()}`}>
                                        <td className="px-4 py-3">{row.county}</td>
                                        <td className="px-4 py-3 text-right tabular">{row.invoice_count}</td>
                                        <td className="px-4 py-3 text-right tabular">{fmtMoney(row.total_sales)}</td>
                                        <td className="px-4 py-3 text-right tabular">{fmtMoney(row.taxable_sales)}</td>
                                        <td className="px-4 py-3 text-right tabular font-semibold text-emerald-600 dark:text-emerald-400">{fmtMoney(row.tax_collected)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="font-semibold bg-secondary/40">
                                    <td className="px-4 py-3">TOTAL</td>
                                    <td className="px-4 py-3 text-right tabular" data-testid="salestax-total-invoices">{report.totals.invoice_count}</td>
                                    <td className="px-4 py-3 text-right tabular">{fmtMoney(report.totals.total_sales)}</td>
                                    <td className="px-4 py-3 text-right tabular">{fmtMoney(report.totals.taxable_sales)}</td>
                                    <td className="px-4 py-3 text-right tabular text-emerald-600 dark:text-emerald-400" data-testid="salestax-total-collected">{fmtMoney(report.totals.tax_collected)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
